import React from 'react';
import { ServiceOrderWithTasks, ServiceOrderStatusColors, ServiceOrderStatusLabels } from '../../types/serviceOrder';

interface ServiceOrderBarProps {
  serviceOrder: ServiceOrderWithTasks;
  position: number;
  width: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

export const ServiceOrderBar: React.FC<ServiceOrderBarProps> = ({
  serviceOrder,
  position,
  width,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick
}) => {
  const status = serviceOrder.status || 'agendado';
  const statusColor = ServiceOrderStatusColors[status];
  const statusLabel = ServiceOrderStatusLabels[status];

  const formatDate = (dateString: string) => {
    const isMidnightUTC = /T00:00:00(?:\.000)?(?:Z|\+00:00)$/.test(dateString);
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      timeZone: isMidnightUTC ? 'UTC' : 'America/Sao_Paulo'
    });
  };

  const calculateProgress = () => {
    if (serviceOrder.totalTasks === 0) return 0;
    const completedTasks = serviceOrder.tasks.filter(task => task.status === 'encerrada').length;
    return Math.round((completedTasks / serviceOrder.totalTasks) * 100);
  };

  const progress = calculateProgress();

  return (
    <>
      <div
        className={`absolute h-full rounded cursor-pointer transition-all duration-200 ${
          isHovered ? 'z-20 transform scale-105' : 'z-10'
        }`}
        style={{
          left: `${position}%`,
          width: `${width}%`,
          minWidth: '24px'
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {/* Service Order Bar */}
        <div className={`w-full h-full rounded ${statusColor} border transition-all duration-200 ${
          isHovered ? 'shadow-lg shadow-blue-500/20' : ''
        }`}>
          {/* Progress bar overlay */}
          {progress > 0 && (
            <div 
              className="h-full bg-white/20 rounded-l"
              style={{ width: `${progress}%` }}
            />
          )}
          
          {/* Service Order Name */}
          <div className="absolute inset-0 flex items-center px-2">
            <div className="text-xs font-medium text-white truncate">
              {serviceOrder.name}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {isHovered && (
        <div
          className="absolute z-30 p-3 bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl min-w-[280px] pointer-events-none"
          style={{
            left: `${Math.min(position, 70)}%`,
            top: '-160px'
          }}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground-primary">
                {serviceOrder.name}
              </h4>
              <span className={`text-xs px-2 py-1 rounded ${statusColor}`}>
                {statusLabel}
              </span>
            </div>
            
            <div className="text-xs text-foreground-secondary">
              {formatDate(serviceOrder.startDate)} - {formatDate(serviceOrder.endDate)}
            </div>
            
            {serviceOrder.description && (
              <div className="text-xs text-foreground-tertiary">
                {serviceOrder.description}
              </div>
            )}
            
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="flex items-center gap-4">
                <div className="text-xs">
                  <span className="text-foreground-tertiary">Tarefas:</span>
                  <span className="text-foreground-primary ml-1">
                    {serviceOrder.totalTasks}
                  </span>
                </div>
                <div className="text-xs">
                  <span className="text-foreground-tertiary">Horas:</span>
                  <span className="text-foreground-primary ml-1">
                    {serviceOrder.totalHoras}h
                  </span>
                </div>
              </div>
              
              <div className="text-xs">
                <span className="text-foreground-tertiary">Progresso:</span>
                <span className="text-foreground-primary ml-1">
                  {progress}%
                </span>
              </div>
            </div>
            
            <div className="text-xs text-blue-400 mt-2">
              Clique para gerenciar
            </div>
          </div>
          
          {/* Tooltip arrow */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900/95" />
        </div>
      )}
    </>
  );
};
